// /weighbridge/settings/barriers — Phase C2 Group 2.
//
// Admin list of barriers / signals (traffic lights, barrier arms,
// sirens, generic signals) configured for the tenant. Each row links
// to the detail page; "Add Barrier" launches detail in /new mode.

import { useNavigate } from 'react-router-dom';
import { Plus, Construction } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import {
  useWbBarriers,
  type WbBarrier,
  type WbBarrierType,
  type WbBarrierProtocol,
} from '../hooks/useWbBarriers';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbBarriersPage.module.css';

const TYPE_LABEL: Record<WbBarrierType, string> = {
  traffic_light: 'Traffic Light',
  barrier_arm: 'Barrier Arm',
  siren: 'Siren',
  generic_signal: 'Generic Signal',
};

const PROTOCOL_LABEL: Record<WbBarrierProtocol, string> = {
  usb_relay_hid: 'USB Relay (HID)',
  modbus_tcp: 'Modbus TCP',
  mqtt: 'MQTT',
  gpio: 'GPIO',
};

export function WbBarriersPage(): JSX.Element {
  const navigate = useNavigate();
  const barriersQuery = useWbBarriers();
  const barriers = barriersQuery.data?.barriers ?? [];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Barriers & Signals"
        subtitle="Configure barriers, traffic lights, sirens and other yard signals."
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/settings/barriers/new')}
          >
            Add Barrier
          </Button>
        }
      />

      {barriersQuery.isError ? (
        <p className={styles.error}>{extractErrorMessage(barriersQuery.error)}</p>
      ) : null}

      <Card flush>
        {barriersQuery.isLoading ? (
          <p className={styles.empty}>Loading…</p>
        ) : barriers.length === 0 ? (
          <EmptyState
            icon={Construction}
            title="No barriers configured"
            description="Add a barrier, traffic light or siren to start automating yard signals."
            action={
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => navigate('/weighbridge/settings/barriers/new')}
              >
                Add Barrier
              </Button>
            }
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Type</th>
                <th>Protocol</th>
                <th>Bridge / Yard</th>
                <th>Default state</th>
                <th>Active</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {barriers.map((b: WbBarrier) => (
                <tr key={b.enc_id}>
                  <td>{b.name}</td>
                  <td className={styles.code}>{b.code}</td>
                  <td>{TYPE_LABEL[b.type]}</td>
                  <td>{PROTOCOL_LABEL[b.protocol]}</td>
                  <td>{b.weighbridge?.name ?? b.yard.name}</td>
                  <td>{b.default_state}</td>
                  <td>{b.is_active ? 'Yes' : 'No'}</td>
                  <td className={styles.actions}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        navigate(
                          `/weighbridge/settings/barriers/${encodeURIComponent(b.enc_id)}`,
                        )
                      }
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
